<template>
  <div class="cpe-credits-history">
    <div class="cpe-credits-history__title-pre">
      {{ preTitle }}
    </div>
    <div class="cpe-credits-history__title">CPE credit history</div>
    <div class="cpe-credits-history__details">
      <div
        v-for="(credit, index) in cpeCreditHistory"
        :key="index"
        class="cpe-credits-history__details-row"
      >
        <div class="cpe-credits-history__details-text">
          <div class="cpe-credits-history__details-title">
            {{ credit.source_type_name }}
          </div>
          <div class="cpe-credits-history__details-date">
            {{ credit.operation_date_formatted }}
          </div>
        </div>
        <div class="cpe-credits-history__details-credits-amount">
          <span
            v-if="credit.valid_credit"
            class="cpe-credits-history__details-credits-amount--plus"
          >
            +
          </span>
          <span
            v-else
            class="cpe-credits-history__details-credits-amount--minus"
          >
            -
          </span>
          <span
            :class="{
              'cpe-credits-history__details-credits-amount--plus':
                credit.valid_credit,
              'cpe-credits-history__details-credits-amount--minus': !credit.valid_credit,
            }"
          >
            {{ credit.credits }} credits
          </span>
        </div>
      </div>
      <ActionLoader v-if="isFetching" :size="0.7" />
      <div ref="list-bottom"></div>
    </div>
  </div>
</template>

<script>
import ActionLoader from '@/components/shared/ActionLoader.vue'
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'CPECreditsHistoryModal',
  components: {
    ActionLoader,
  },
  data() {
    return {
      cpeCreditHistory: [], // Initialize with an empty array or appropriate value
      cpeCreditHistoryPage: 1, // Page number for pagination
      isFetching: false,
      observer: null,
    }
  },
  computed: {
    ...mapGetters({
      cpeGlobalCredits: 'cpeProgram/cpeGlobalCredits',
      cpeCreditHistoryFetched: 'cpeProgram/cpeCreditHistory',
      cpeCreditHistoryHasNextPage: 'cpeProgram/cpeCreditHistoryHasNextPage',
    }),
    preTitle() {
      return `${this.cpeGlobalCredits} CPE credit${
        this.cpeGlobalCredits === 1 ? '' : 's'
      } available`
    },
  },
  mounted() {
    this.initIntersectionObserver()
  },
  created() {
    this.cpeCreditHistory = this.cpeCreditHistoryFetched
  },
  async beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect()
    }
    this.cpeCreditHistoryPage = 1
    await this.getCPECreditsHistory(1)
  },
  methods: {
    ...mapActions({
      getCPECreditsHistory: 'cpeProgram/getCPECreditsHistory',
    }),
    initIntersectionObserver() {
      const options = {
        root: null, // Use the viewport as the root
        rootMargin: '0px',
        threshold: 0.1, // Trigger when 10% of the element is visible
      }

      this.observer = new IntersectionObserver(this.handleIntersect, options)
      this.$nextTick(() => {
        const listBottom = this.$refs['list-bottom']
        if (listBottom) {
          this.observer.observe(listBottom)
        }
      })
    },
    handleIntersect(entries) {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Bottom of the list is visible, load more data
          if (this.cpeCreditHistoryHasNextPage && !this.isFetching) {
            this.refreshDetails()
          }
        }
      })
    },
    async refreshDetails() {
      const nextPage = ++this.cpeCreditHistoryPage
      this.isFetching = true
      await this.getCPECreditsHistory(nextPage)
      this.cpeCreditHistory = this.cpeCreditHistory.concat(
        this.cpeCreditHistoryFetched
      )
      this.isFetching = false

      // Renew the observer
      if (this.observer) {
        this.observer.disconnect()
      }
      if (this.cpeCreditHistoryHasNextPage) {
        this.$nextTick(() => {
          this.initIntersectionObserver()
        })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.cpe-credits-history {
  $this: &;
  margin: 40px 0 40px 0;
  @include mobile {
    width: 700px;
    max-height: 400px;
  }
  &__title {
    color: var(--white);
    font-size: rem(40px);
    font-style: normal;
    font-weight: 700;
    line-height: rem(52px);
    &-pre {
      color: var(--white);
      font-size: rem(16px);
      font-style: normal;
      font-weight: 450;
      line-height: rem(24px);
    }
  }

  &__details {
    gap: 10px;
    display: flex;
    flex-flow: column;
    margin-top: 20px;
    height: 300px;
    overflow-y: scroll;
    &-row {
      display: flex;
      flex-flow: row;
      gap: 10px;
      justify-content: space-between;
      padding: 8px 15px 8px 12px;
      border-radius: 5px;
      background: var(--glass-mid-gray);
    }
    &-text {
      text-align: left;
    }
    &-title {
      color: var(--white);
      font-feature-settings: 'liga' off, 'clig' off;
      text-overflow: ellipsis;
      font-size: rem(14px);
      font-style: normal;
      font-weight: 450;
      line-height: rem(20px);
    }
    &-date {
      color: var(--light-blue-gray);
      font-size: rem(10px);
      font-style: normal;
      font-weight: 450;
      line-height: rem(12px);
    }
    &-credits {
      &-amount {
        display: flex;
        align-items: center;
        &--plus {
          color: var(--positive-light);
        }
        &--minus {
          color: var(--carnation-sonar);
        }
      }
    }
    &::-webkit-scrollbar {
      width: 2px;
    }

    &::-webkit-scrollbar-thumb {
      background-color: var(--perfect-gray);
      border-radius: 10px;
    }

    &::-webkit-scrollbar-thumb:hover {
      background-color: var(--purple-pale);
    }

    &::-webkit-scrollbar-track {
      background-color: var(--glass-mid-gray);
    }
  }
}
</style>
